// ==============================================
// Admin › Feedback: reports customers send from their Scout
// ==============================================
//
// An inbox, as a table: what was reported (reference, kind, severity, app),
// who sent it, its status with the one badge that matters (who it waits on),
// its owner and its last activity. Rows alternate shade. Tabs by state, with
// "Needs a reply" first; then category, owner and search (a reference too).
// Latest activity first, so a customer's new comment brings its report to the
// top. Each report opens on its own page (AdminFeedbackDetailPage). The whole
// view lives in the address, so Back from a report returns to the same list.
// Below 1024 px wide each row becomes a card.

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { api } from '../../services/api';
import { usePageSize } from '../../hooks/usePageSize';
import { PageSizeSelect } from '../../components/ui/PageSizeSelect';
import { formatDateTime } from '../../utils/formatters';
import {
  FEEDBACK_CATEGORIES,
  type FeedbackRow,
  type FeedbackStaffMember,
} from '../../types/feedback';
import {
  AppLabel,
  Avatar,
  CATEGORY_LOOK,
  FEEDBACK_TABS,
  SEVERITY_INK,
  SEVERITY_LOOK,
  StatusDot,
  WaitingFlag,
  feedbackRef,
  firstLine,
  personName,
  readFeedbackListState,
  shortAgo,
  writeFeedbackListState,
  type FeedbackListState,
  type FeedbackTab,
} from './feedbackShared';

const filterClass =
  'h-9 rounded-md border border-slate-700 bg-slate-900 pl-3 pr-8 text-sm text-slate-100 hover:border-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500';

/** The table's columns from 1024 px: the report takes what is left. Owner shows its name from 1280 px. */
const COLUMNS = 'lg:grid lg:grid-cols-[minmax(0,1fr)_130px_130px_44px_76px] lg:gap-x-3 xl:grid-cols-[minmax(0,1fr)_190px_150px_112px_88px] xl:gap-x-4';

const EMPTY_WORDS: Record<FeedbackTab, string> = {
  waiting: 'Nothing is waiting on a reply.',
  open: 'No open reports.',
  in_progress: 'Nothing in progress.',
  resolved: 'No resolved reports.',
  closed: 'No closed reports.',
  '': 'No reports yet.',
};

/** Who owns it: a person here, the old system's name, or nobody. */
function ownerWords(row: FeedbackRow): string {
  if (row.assignee) return personName(row.assignee);
  if (row.legacy_assigned_to) return `${row.legacy_assigned_to} (old system)`;
  return 'Nobody';
}

/** A person's first name, for the narrow Owner column. */
function firstName(row: FeedbackRow): string {
  if (!row.assignee) return row.legacy_assigned_to ?? 'Nobody';
  return row.assignee.name?.trim().split(/\s+/)[0] || row.assignee.email;
}

/** Who sent it: the boat (or the person) on top, the account's email under it. */
function sender(row: FeedbackRow): { top: string | null; under: string } {
  const name = row.account ? personName(row.account) : null;
  if (row.vessel_name) return { top: row.vessel_name, under: row.account?.email ?? 'Sent before accounts' };
  if (row.account) return { top: name, under: row.account.name ? row.account.email : '' };
  return { top: null, under: 'Sent before accounts' };
}

/** "#307456 · Bug · Major · [glyph] VortexNav": what was reported, on one line. */
function Kind({ row, withApp = true, extra }: { row: FeedbackRow; withApp?: boolean; extra?: string | null }) {
  const severity = row.category === 'bug' && row.severity ? SEVERITY_LOOK[row.severity] : null;
  const dot = <span aria-hidden="true" className="text-slate-600">·</span>;
  return (
    <span className="flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap text-[13px] text-slate-400">
      <span className="shrink-0 font-mono text-slate-400">{feedbackRef(row.reference)}</span>
      {dot}
      <span className="shrink-0">{(CATEGORY_LOOK[row.category] ?? CATEGORY_LOOK.other).label}</span>
      {severity && <>{dot}<span className={clsx('shrink-0', SEVERITY_INK[row.severity!])}>{severity.label}</span></>}
      {withApp && row.affected_app && <>{dot}<AppLabel app={row.affected_app} className="min-w-0" /></>}
      {extra && <>{dot}<span className="min-w-0 truncate">{extra}</span></>}
    </span>
  );
}

/** Under the status: the badge for who it waits on, or that the customer closed it. */
function StatusNote({ row }: { row: FeedbackRow }) {
  if (row.waiting) return <WaitingFlag row={row} />;
  if (row.status === 'closed' && row.closed_by_customer) return <span className="text-[13px] text-slate-400">By the customer</span>;
  return null;
}

function Row({ row, striped, from }: { row: FeedbackRow; striped: boolean; from: string }) {
  const title = firstLine(row.description) || 'No description';
  const who = sender(row);
  const owner = ownerWords(row);
  const updated = `Last activity ${formatDateTime(row.updated_at)}`;
  return (
    <li className={clsx(striped && 'bg-slate-800/40')}>
      <Link
        to={`/admin/feedback/${row.id}`}
        state={{ from }}
        className={clsx(
          'block px-4 py-3 transition-colors hover:bg-slate-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500',
          'lg:h-[60px] lg:items-center lg:py-0',
          COLUMNS,
        )}
      >
        {/* The report */}
        <span className="block min-w-0">
          <span className="line-clamp-2 break-words text-[15px] font-medium text-white lg:line-clamp-none lg:truncate lg:text-sm lg:text-slate-100">{title}</span>
          <span className="mt-1 block lg:mt-0.5">
            {/* On a card the boat stands in for the From column. */}
            <span className="lg:hidden"><Kind row={row} withApp={false} extra={who.top ?? who.under} /></span>
            <span className="hidden lg:block"><Kind row={row} /></span>
          </span>
        </span>

        {/* From */}
        <span className="hidden min-w-0 lg:block">
          <span className={clsx('block truncate text-sm', who.top ? 'text-slate-100' : 'text-slate-500')}>{who.top ?? 'Sender not known'}</span>
          {who.under && <span className="mt-0.5 block truncate text-[13px] text-slate-400">{who.under}</span>}
        </span>

        {/* Status, and who it waits on. On a card: with the owner and the time. */}
        <span className="mt-2 flex min-w-0 items-center gap-2 lg:mt-0 lg:block">
          <StatusDot status={row.status} className="text-[13px] lg:text-sm" />
          <span className="lg:mt-1 lg:block"><StatusNote row={row} /></span>
          <span className="ml-auto flex shrink-0 items-center gap-2 lg:hidden">
            <span title={`Owner: ${owner}`}><Avatar person={row.assignee} size="sm" /><span className="sr-only">Owner: {owner}</span></span>
            <span className="text-[13px] text-slate-400" title={updated}>{shortAgo(row.updated_at)}</span>
          </span>
        </span>

        {/* Owner */}
        <span className="hidden min-w-0 items-center gap-2 lg:flex" title={owner}>
          <Avatar person={row.assignee} size="sm" />
          <span className={clsx('hidden truncate text-sm xl:inline', row.assignee ? 'text-slate-200' : 'text-slate-500')}>{firstName(row)}</span>
          <span className="sr-only xl:hidden">Owner: {owner}</span>
        </span>

        {/* Updated */}
        <span className="hidden whitespace-nowrap text-right text-[13px] tabular-nums text-slate-400 lg:block" title={updated}>
          {shortAgo(row.updated_at)}
        </span>
      </Link>
    </li>
  );
}

function SkeletonRows() {
  return (
    <ul aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <li key={i} className={clsx('px-4 py-3 lg:flex lg:h-[60px] lg:items-center lg:py-0', i % 2 === 1 && 'bg-slate-800/40')}>
          <span className="block w-full space-y-2">
            <span className="block h-4 w-3/5 animate-pulse rounded bg-slate-700/70" />
            <span className="block h-3.5 w-2/5 animate-pulse rounded bg-slate-700/50" />
          </span>
        </li>
      ))}
    </ul>
  );
}

export function AdminFeedbackPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The address as last written, so a search timer never writes stale filters back.
  const liveParams = useRef(searchParams);
  liveParams.current = searchParams;
  const view = readFeedbackListState(searchParams);

  const update = (patch: Partial<FeedbackListState>) => {
    const next = { ...readFeedbackListState(liveParams.current), ...patch };
    if (patch.page === undefined) next.page = 1;
    const written = writeFeedbackListState(next);
    liveParams.current = written;
    setSearchParams(written, { replace: true });
  };

  // The box is typed into directly; the address and the fetch follow 300 ms later.
  const [search, setSearch] = useState(view.q);
  useEffect(() => { setSearch(view.q); }, [view.q]);
  useEffect(() => {
    if (search === view.q) return;
    const t = setTimeout(() => update({ q: search }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const [pageSize, setPageSize] = usePageSize('feedback');

  const { data: staff } = useQuery({
    queryKey: ['admin', 'feedback', 'staff'],
    queryFn: () => api.adminFeedbackStaff(),
    staleTime: 5 * 60_000,
  });

  const params = {
    status: view.status && view.status !== 'waiting' ? view.status : undefined,
    waiting: view.status === 'waiting' ? (1 as const) : undefined,
    category: view.category || undefined,
    assignee: view.owner || undefined,
    q: view.q.trim() || undefined,
    page: view.page,
    limit: pageSize,
  };
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['admin', 'feedback', 'list', params],
    queryFn: () => api.adminFeedbackList(params),
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const rows = data?.rows ?? [];
  const here = location.pathname + location.search;

  const ownerOptions: { value: string; label: string }[] = [
    { value: '', label: 'Any owner' },
    { value: 'me', label: 'Mine' },
    { value: 'none', label: 'No owner' },
    ...(staff ?? []).map((s: FeedbackStaffMember) => ({ value: s.id, label: personName(s) })),
  ];
  // A link may name someone who is no longer staff; keep the select honest.
  if (view.owner && !ownerOptions.some((o) => o.value === view.owner)) {
    ownerOptions.push({ value: view.owner, label: 'Someone no longer listed' });
  }

  const pagerButton = 'flex h-8 w-8 items-center justify-center rounded-md border border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:text-slate-600 disabled:hover:border-slate-700';

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Feedback</h1>
          <p className="mt-1 text-sm text-slate-400">Reports and conversations from customers' Scouts.</p>
        </div>
        <label className="relative block w-full sm:w-[300px]">
          <span className="sr-only">Search</span>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Report, boat, email or serial"
            className="h-9 w-full rounded-md border border-slate-700 bg-slate-900 pl-[38px] pr-3 text-sm text-white placeholder:text-slate-400 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </label>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-slate-700">
        <div role="tablist" aria-label="Reports by state" className="-mb-px flex max-w-full overflow-x-auto">
          {FEEDBACK_TABS.map((t) => {
            const on = view.status === t.value;
            const n = data?.counts?.[t.count];
            const loud = t.value === 'waiting' && !!n;
            return (
              <button
                key={t.value || 'all'}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => update({ status: t.value })}
                className={clsx(
                  'inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors',
                  on ? 'border-cyan-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-100',
                )}
              >
                {t.label}
                {n !== undefined && (
                  <span
                    className={clsx(
                      'text-[13px] font-semibold tabular-nums',
                      loud ? 'rounded bg-cyan-400 px-1.5 text-slate-900' : on ? 'text-white' : 'text-slate-300',
                    )}
                  >
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex w-full items-center gap-2 pb-2 sm:w-auto">
          <label className="min-w-0 flex-1 sm:flex-none">
            <span className="sr-only">Category</span>
            <select
              value={view.category}
              onChange={(e) => update({ category: e.target.value as FeedbackListState['category'] })}
              className={clsx(filterClass, 'w-full')}
            >
              <option value="">All categories</option>
              {FEEDBACK_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LOOK[c].label}</option>)}
            </select>
          </label>
          <label className="min-w-0 flex-1 sm:flex-none">
            <span className="sr-only">Owner</span>
            <select value={view.owner} onChange={(e) => update({ owner: e.target.value })} className={clsx(filterClass, 'w-full')}>
              {ownerOptions.map((o) => <option key={o.value || 'anyone'} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={() => refetch()}
            title="Refresh"
            aria-label="Refresh"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500 hover:text-white"
          >
            <ArrowPathIcon className={clsx('h-[18px] w-[18px]', isFetching && 'animate-spin')} aria-hidden="true" />
          </button>
        </div>
      </div>

      <section aria-label="Reports" className="overflow-hidden rounded-lg border border-slate-700">
        <div aria-hidden="true" className={clsx('hidden h-10 items-center border-b border-slate-700 bg-slate-800 px-4 text-[13px] font-semibold uppercase tracking-wide text-slate-400', COLUMNS)}>
          <span>Report</span>
          <span>From</span>
          <span>Status</span>
          {/* From 1280 px the column has room for its word; below that, a person glyph heads the avatars. */}
          <span className="hidden xl:inline">Owner</span>
          <UserIcon className="h-4 w-4 xl:hidden" />
          <span className="text-right">Updated</span>
        </div>

        {isLoading && <SkeletonRows />}

        {isError && !data && (
          <div role="alert" className="px-5 py-10 text-center text-[15px] text-slate-200">
            The reports did not load.{' '}
            <button type="button" className="font-medium text-cyan-300 underline" onClick={() => refetch()}>Try again</button>
          </div>
        )}

        {data && rows.length === 0 && (
          <div className="px-5 py-14 text-center">
            <ChatBubbleLeftRightIcon className="mx-auto h-9 w-9 text-slate-600" aria-hidden="true" />
            <p className="mt-3 text-[15px] text-slate-300">
              {view.q || view.category || view.owner ? 'No reports match.' : EMPTY_WORDS[view.status]}
            </p>
          </div>
        )}

        {rows.length > 0 && (
          <ul className={clsx('transition-opacity', isFetching && !isLoading && 'opacity-70')}>
            {rows.map((row, i) => <Row key={row.id} row={row} striped={i % 2 === 1} from={here} />)}
          </ul>
        )}

        {data && total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-700 bg-slate-800 px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
              <span className="tabular-nums">
                {(view.page - 1) * pageSize + 1}–{Math.min(view.page * pageSize, total)} of {total}
              </span>
              <span aria-hidden="true" className="text-slate-600">·</span>
              <PageSizeSelect value={pageSize} onChange={(size) => { setPageSize(size); update({}); }} />
            </div>
            {total > pageSize && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => update({ page: view.page - 1 })}
                  disabled={view.page <= 1}
                  aria-label="Previous page"
                  title="Previous page"
                  className={pagerButton}
                >
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                </button>
                <span className="text-sm tabular-nums text-slate-200">Page {view.page} of {lastPage}</span>
                <button
                  type="button"
                  onClick={() => update({ page: view.page + 1 })}
                  disabled={view.page >= lastPage}
                  aria-label="Next page"
                  title="Next page"
                  className={pagerButton}
                >
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default AdminFeedbackPage;
